import { useTranslation } from 'react-i18next'
import { displayOutcome } from './reconciliationState'
import { cell, formatTimestamp, makeVocab, outcomeClass, placeholder, separator } from './reconciliationVocab'
import { TablePaginator } from './TablePaginator'
import { Pill } from './Pill'

const T = 'cycle_counts.events.reconciliation'
export const REMOVAL_PER_PAGE = 100
const COLUMN_CLASS = {
  product: 'text-left whitespace-nowrap',
  lot: 'text-left',
  existing: 'text-right tabular-nums',
  planned: 'text-right tabular-nums',
  since: 'text-left whitespace-nowrap',
  executed: 'text-right tabular-nums',
  outcome: 'text-left',
  reason: 'text-left',
}
const COLUMNS = Object.keys(COLUMN_CLASS)

// The fourth card of a store-wide proposal: one row per (product, lot) of the store's recoverable
// LOST pool at freeze, not a bay row — it lives on the event, pages on its own and is rendered
// below the bay table. `existing` is the pool when the proposal froze, `planned` what the batch
// may remove at most (oldest first, never more); `since` is approximate by contract.
export const CoverageEventRemovalCard = ({ removal, page, onPage, disabled = false }) => {
  const { i18n } = useTranslation()
  const vocab = makeVocab(i18n, T)
  const items = removal?.items ?? []
  const meta = removal?.meta ?? { total_pages: 0, current_page: 1 }

  return (
    <section data-testid="removal-card" className="rounded border border-gray-200 p-3 space-y-2">
      <h4 className="text-sm font-medium text-gray-900">{i18n.t(`${T}.kind.removal`)}</h4>
      <p className="text-xs text-gray-500">{i18n.t(`${T}.removal.hint`)}</p>
      {/* The document's own count, not the page's rows: a page past the end (the one round-trip
          before the pane's page reset lands) is not an empty document. */}
      {(removal?.meta?.total_count ?? items.length) === 0 ? (
        <p className="text-sm text-gray-500">{i18n.t(`${T}.removal.none`)}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                {COLUMNS.map(c => (
                  <th key={c} className={`px-3 py-1 text-xs text-gray-500 ${COLUMN_CLASS[c]}`}>
                    {i18n.t(`${T}.columns.${c}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const outcome = displayOutcome(item)
                const reason = typeof item.reason === 'string' ? vocab('reasons', item.reason) : ''
                const unexecuted = item.executed_units != null ? (item.quantity ?? 0) - item.executed_units : 0
                const parts = [reason || null, outcome !== 'pending' && unexecuted > 0 ? i18n.t(`${T}.unexecuted_hint`, { count: unexecuted }) : null].filter(
                  Boolean,
                )
                return (
                  <tr key={item.key ?? `${item.product_id}-${item.lot_id ?? '-'}-${index}`}>
                    <td className={`${cell} text-gray-900 ${COLUMN_CLASS.product}`}>{item.product_sku ?? `#${item.product_id}`}</td>
                    <td className={`${cell} text-gray-500 ${COLUMN_CLASS.lot}`}>{item.lot_code ?? item.lot_id ?? '-'}</td>
                    <td className={`${cell} ${COLUMN_CLASS.existing}`}>{item.existing ?? placeholder}</td>
                    <td className={`${cell} ${COLUMN_CLASS.planned}`}>{item.quantity ?? placeholder}</td>
                    <td className={`${cell} text-gray-500 ${COLUMN_CLASS.since}`}>{formatTimestamp(item.since, i18n.language) || placeholder}</td>
                    <td className={`${cell} ${COLUMN_CLASS.executed}`}>{item.executed_units ?? placeholder}</td>
                    <td className={`${cell} ${COLUMN_CLASS.outcome}`}>
                      <Pill className={outcomeClass(outcome)}>{vocab('outcome', outcome)}</Pill>
                    </td>
                    <td className={`${cell} text-gray-500 ${COLUMN_CLASS.reason}`}>
                      {parts.length > 0 ? (
                        <span className="inline-flex flex-wrap items-center gap-x-2">
                          {parts.map((part, i) => (
                            <span key={i} className="whitespace-nowrap">
                              {part}
                              {i < parts.length - 1 && separator}
                            </span>
                          ))}
                        </span>
                      ) : (
                        placeholder
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <TablePaginator page={page} meta={meta} onPage={onPage} disabled={disabled} />
    </section>
  )
}
